import {
  ArrowLeftOutlined,
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  InboxOutlined,
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  SearchOutlined
} from "@ant-design/icons"
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  Grid,
  Popover,
  Segmented,
  Select,
  Skeleton,
  Tag,
  Tooltip
} from "antd"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import { unavailableProps } from "../../../utils/unavailable-control.ts"
import {
  type MessageFilterOptions,
  getMessageFilterOptions,
  getMessages
} from "../api/messages.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import { useComposerLauncher } from "../composer/CommunicateButton.tsx"
import { targetToPrefill } from "../composer/composer-target.ts"
import { onMessagesSent } from "../composer/messages-sent.ts"
import { MessageStatusDot, sourceOf } from "../hub/message-status.tsx"
import { SourceTag } from "../hub/ui.tsx"
import { useStamp, zoneAbbreviation } from "../hub/use-stamp.ts"
import {
  MESSAGE_SOURCES,
  MESSAGE_STATUSES,
  type MessageDetail,
  type MessageRow
} from "../types/messages.types.ts"
import { ContactPreferencesPanel } from "./ContactPreferencesPanel.tsx"
import { ContactStrip } from "./ContactStrip.tsx"
import { RecordMessagePane } from "./RecordMessagePane.tsx"
import {
  QueueRowActions,
  queueRowPadding,
  useWhileMounted
} from "../messages/QueueActions.tsx"
import {
  type CommContact,
  type CommRecord,
  contactKey
} from "./record-contacts.ts"
import {
  type ChannelFilter,
  DEFAULT_ORDERING,
  EMPTY_FILTERS,
  QUEUE_REASONS,
  type RecordFilters,
  type RecordView,
  activeFilterCount,
  queueCountQuery,
  recipientChoices,
  recordQuery
} from "./record-query.ts"

type RecordMessageRow = MessageRow

type LoadState = "loading" | "ready" | "failed" | "forbidden"

const ChannelGlyph = ({ channel }: { channel: string }) =>
  channel === "sms" ? (
    <MessageOutlined aria-hidden />
  ) : (
    <MailOutlined aria-hidden />
  )

const useZoned = () => {
  const { date, time, timezone } = useStamp()
  return (value: string) => ({
    date: date(value),
    time: `${time(value)} ${zoneAbbreviation(timezone, new Date(value))}`
  })
}

const headlineOf = (row: RecordMessageRow, t: (key: string) => string) =>
  row.subject ||
  (row.channel === "sms" && row.body_preview
    ? row.body_preview
    : t("commHub.messages.textMessage"))

const MessageItem = ({
  row,
  selected,
  fromLeadStage,
  onOpen,
  onActionDone
}: {
  row: RecordMessageRow
  selected: boolean
  fromLeadStage: boolean
  onOpen: () => void
  onActionDone?: () => void
}) => {
  const { t } = useTranslation()
  const zoned = useZoned()
  const stampAt =
    row.status === "queued" && row.not_before
      ? row.not_before
      : (row.sent_at ?? row.created)
  const stampLabel =
    row.status === "queued" && row.not_before
      ? t("recordComm.list.planned")
      : row.sent_at
        ? null
        : t("recordComm.list.created")
  const when = zoned(stampAt)
  return (
    <li className="relative">
      <button
        type="button"
        onClick={onOpen}
        aria-current={selected ? "true" : undefined}
        className={`flex w-full cursor-pointer gap-3 border-0 border-b border-l-4 border-solid border-b-slate-100 px-4 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#2A7D8C] ${queueRowPadding(row)} ${
          selected
            ? "border-l-[#2A7D8C] bg-[var(--dk-selected,#EEF7F6)]"
            : "border-l-transparent bg-surface hover:bg-slate-50"
        }`}
      >
        <span className="mt-0.5 text-lg text-[var(--dk-primary-text,#2A7D8C)]">
          <ChannelGlyph channel={row.channel} />
          <span className="sr-only">{t(`commHub.channel.${row.channel}`)}</span>
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-start justify-between gap-2">
            <span className="line-clamp-1 font-semibold text-slate-900">
              {headlineOf(row, t)}
            </span>
            <span className="shrink-0 text-right text-xs text-slate-500">
              {stampLabel && <span className="block">{stampLabel}</span>}
              <span className="block">{when.date}</span>
              <span className="block">{when.time}</span>
            </span>
          </span>
          <span className="truncate text-sm text-slate-600">
            {t("recordComm.list.to", {
              name: row.contact_name || row.to_address || "—"
            })}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <SourceTag source={sourceOf(row)} />
            <MessageStatusDot status={row.status} />
            {fromLeadStage && (
              <Tag className="m-0 text-[11px]">
                {t("recordComm.list.fromLeadStage")}
              </Tag>
            )}
          </span>
        </span>
      </button>
      <QueueRowActions row={row} onDone={onActionDone} />
    </li>
  )
}

const QueueItem = ({
  row,
  selected,
  onOpen,
  onActionDone
}: {
  row: RecordMessageRow
  selected: boolean
  onOpen: () => void
  onActionDone?: () => void
}) => {
  const { t } = useTranslation()
  const zoned = useZoned()
  const reason = row.queue_reason || row.hold_reason || "due"
  const planned = row.not_before ? zoned(row.not_before) : null
  return (
    <li className="relative">
      <button
        type="button"
        onClick={onOpen}
        aria-current={selected ? "true" : undefined}
        className={`flex w-full cursor-pointer gap-3 border-0 border-b border-l-4 border-solid border-b-slate-100 px-4 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#2A7D8C] ${queueRowPadding(row)} ${
          selected
            ? "border-l-[#2A7D8C] bg-[var(--dk-selected,#EEF7F6)]"
            : "border-l-transparent bg-surface hover:bg-slate-50"
        }`}
      >
        <span className="mt-0.5 text-lg text-[var(--dk-primary-text,#2A7D8C)]">
          <ChannelGlyph channel={row.channel} />
          <span className="sr-only">{t(`commHub.channel.${row.channel}`)}</span>
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="line-clamp-1 font-semibold text-slate-900">
            {headlineOf(row, t)}
          </span>
          <span className="truncate text-sm text-slate-600">
            {t("recordComm.list.to", {
              name: row.contact_name || row.to_address || "—"
            })}
          </span>
          <span className="text-sm text-slate-800">
            {t(`recordComm.queue.reason.${reason}`, {
              defaultValue: reason
            })}
          </span>
          <span className="text-xs text-slate-600">
            {planned
              ? t("recordComm.queue.plannedFor", {
                  date: planned.date,
                  time: planned.time
                })
              : reason === "sending"
                ? t("recordComm.queue.sendingNow")
                : reason === "due"
                  ? t("recordComm.queue.nextAvailable")
                  : t("recordComm.queue.noPlannedTime")}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <SourceTag source={sourceOf(row)} />
            <span className="text-slate-600">
              {row.sender_name
                ? t("recordComm.queue.by", { name: row.sender_name })
                : t("communication.messages.automated")}
            </span>
          </span>
        </span>
      </button>
      <QueueRowActions row={row} onDone={onActionDone} />
    </li>
  )
}

const StateBlock = ({
  icon,
  title,
  body,
  action
}: {
  icon: React.ReactNode
  title: string
  body?: string
  action?: React.ReactNode
}) => (
  <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
    <span aria-hidden className="text-3xl text-slate-300">
      {icon}
    </span>
    <p className="m-0 font-semibold text-slate-900">{title}</p>
    {body && <p className="m-0 max-w-sm text-sm text-slate-600">{body}</p>}
    {action}
  </div>
)

/**
 * A lead's or project's email and SMS: who they go to, what that person
 * allows, and every message linked to this record with its delivery.
 */
export const RecordCommunication = ({
  record,
  contacts,
  wonAt
}: {
  record: CommRecord
  contacts: CommContact[]
  /** A project's conversion stamp; messages before it were sent to the lead. */
  wonAt?: string | null
}) => {
  const { t } = useTranslation()
  const rights = useCommunicationRights()
  const screens = Grid.useBreakpoint()
  const wide = !!screens.lg
  const noun = t(`recordComm.noun.${record.type}`)

  const [selectedContactKey, setSelectedContactKey] = useState<string | null>(
    null
  )
  const contact =
    contacts.find((item) => contactKey(item.ref) === selectedContactKey) ??
    contacts[0]
  const [prefsOpen, setPrefsOpen] = useState(false)

  const [view, setView] = useState<RecordView>("all")
  const [filters, setFilters] = useState<RecordFilters>(EMPTY_FILTERS)
  const [ordering, setOrdering] = useState(DEFAULT_ORDERING.all)
  const [rows, setRows] = useState<RecordMessageRow[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [state, setState] = useState<LoadState>("loading")
  const [loadingMore, setLoadingMore] = useState(false)
  const [queueCount, setQueueCount] = useState<number | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [searchReset, setSearchReset] = useState(0)
  const [pickers, setPickers] = useState<MessageFilterOptions>({
    senders: [],
    campaigns: []
  })
  const generation = useRef(0)
  const recordRef = useRef(record)
  recordRef.current = record

  const load = useCallback(
    async (nextPage: number) => {
      const current = ++generation.current
      if (nextPage === 1) setState("loading")
      else setLoadingMore(true)
      const response = await getMessages(
        recordQuery(recordRef.current, view, filters, ordering, nextPage)
      )
      if (current !== generation.current) return
      setLoadingMore(false)
      if (!response.ok || !response.data) {
        if (nextPage === 1)
          setState(response.status === 403 ? "forbidden" : "failed")
        return
      }
      const results = response.data.results as RecordMessageRow[]
      setRows((previous) =>
        nextPage === 1 ? results : [...previous, ...results]
      )
      setCount(response.data.count)
      setPage(nextPage)
      setState("ready")
    },
    [view, filters, ordering]
  )

  const loadQueueCount = useCallback(async () => {
    const response = await getMessages(queueCountQuery(recordRef.current))
    setQueueCount(response.ok && response.data ? response.data.count : null)
  }, [])

  useEffect(() => {
    void load(1)
  }, [load, record.id])

  const afterQueueAction = useWhileMounted(() => {
    void load(1)
    void loadQueueCount()
  })

  useEffect(() => {
    void loadQueueCount()
  }, [loadQueueCount, record.id])

  useEffect(
    () =>
      onMessagesSent(() => {
        void load(1)
        void loadQueueCount()
      }),
    [load, loadQueueCount]
  )

  useEffect(() => {
    let cancelled = false
    void getMessageFilterOptions().then((response) => {
      if (!cancelled && response.ok && response.data) setPickers(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!wide || state !== "ready") return
    if (rows.length === 0) setSelected(null)
    else if (!selected || !rows.some((row) => row.id === selected))
      setSelected(rows[0].id)
  }, [wide, state, rows, selected])

  const composer = useComposerLauncher(() => {
    void load(1)
    void loadQueueCount()
  })
  const newMessage = (detail?: MessageDetail) => {
    const ids = detail
      ? [
          detail.customer,
          (detail as MessageDetail & { subcontractor?: string | null })
            .subcontractor,
          (detail as MessageDetail & { supplier?: string | null }).supplier,
          detail.lead
        ].filter(Boolean)
      : []
    const to = contacts.find((item) => ids.includes(item.ref.id)) ?? contact
    if (!to) return
    composer.open(
      targetToPrefill({
        record: { type: record.type, id: record.id },
        recordLabel: record.label,
        recipient: to.ref,
        recipientName: to.name,
        channel: detail?.channel
      })
    )
  }

  const switchView = (next: RecordView) => {
    setView(next)
    setOrdering(DEFAULT_ORDERING[next])
    setSelected(null)
  }
  const patch = (changes: Partial<RecordFilters>) =>
    setFilters((current) => ({ ...current, ...changes }))
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setSearchReset((n) => n + 1)
  }

  const filterCount = activeFilterCount(filters, view)
  const recipients = recipientChoices(contacts)
  const senderName = (id: string) =>
    id === "none"
      ? t("commHub.messages.filters.automated")
      : (pickers.senders.find((sender) => sender.id === id)?.name ?? id)

  const chips: Array<{ key: string; label: string; remove: () => void }> = [
    ...(filters.q.trim()
      ? [
          {
            key: "q",
            label: `“${filters.q.trim()}”`,
            remove: () => {
              patch({ q: "" })
              setSearchReset((n) => n + 1)
            }
          }
        ]
      : []),
    ...(filters.channel
      ? [
          {
            key: "channel",
            label: t(`commHub.channel.${filters.channel}`),
            remove: () => patch({ channel: "" })
          }
        ]
      : []),
    ...(view === "all"
      ? filters.status.map((status) => ({
          key: `status:${status}`,
          label: t(`communication.messages.status.${status}`),
          remove: () =>
            patch({ status: filters.status.filter((item) => item !== status) })
        }))
      : filters.reason.map((reason) => ({
          key: `reason:${reason}`,
          label: t(`recordComm.queue.reason.${reason}`),
          remove: () =>
            patch({ reason: filters.reason.filter((item) => item !== reason) })
        }))),
    ...filters.source.map((source) => ({
      key: `source:${source}`,
      label: t(`commHub.source.${source}`),
      remove: () =>
        patch({ source: filters.source.filter((item) => item !== source) })
    })),
    ...filters.sender.map((id) => ({
      key: `sender:${id}`,
      label: t("commHub.messages.filters.sentByChip", { name: senderName(id) }),
      remove: () =>
        patch({ sender: filters.sender.filter((item) => item !== id) })
    })),
    ...(filters.range
      ? [
          {
            key: "range",
            label: `${filters.range[0].format("MMM D")} – ${filters.range[1].format("MMM D, YYYY")}`,
            remove: () => patch({ range: null })
          }
        ]
      : []),
    ...(filters.recipient
      ? [
          {
            key: "recipient",
            label: t("recordComm.filters.toChip", {
              name:
                contacts.find(
                  (item) => contactKey(item.ref) === filters.recipient
                )?.name ?? ""
            }),
            remove: () => patch({ recipient: null })
          }
        ]
      : [])
  ]

  const filterPanel = (
    <div className="flex w-[300px] flex-col gap-3">
      {view === "all" ? (
        <label className="flex flex-col gap-1 text-sm">
          {t("commHub.messages.filters.status")}
          <Select
            aria-label={t("commHub.messages.filters.status")}
            mode="multiple"
            allowClear
            placeholder={t("commHub.messages.filters.allStatuses")}
            value={filters.status}
            onChange={(status: string[]) => patch({ status })}
            options={MESSAGE_STATUSES.map((status) => ({
              value: status,
              label: t(`communication.messages.status.${status}`)
            }))}
          />
        </label>
      ) : (
        <label className="flex flex-col gap-1 text-sm">
          {t("recordComm.filters.reason")}
          <Select
            aria-label={t("recordComm.filters.reason")}
            mode="multiple"
            allowClear
            placeholder={t("recordComm.filters.anyReason")}
            value={filters.reason}
            onChange={(reason: string[]) => patch({ reason })}
            options={QUEUE_REASONS.map((reason) => ({
              value: reason,
              label: t(`recordComm.queue.reason.${reason}`)
            }))}
          />
        </label>
      )}
      <label className="flex flex-col gap-1 text-sm">
        {t("commHub.messages.filters.source")}
        <Select
          aria-label={t("commHub.messages.filters.source")}
          mode="multiple"
          allowClear
          placeholder={t("commHub.messages.filters.allSources")}
          value={filters.source}
          onChange={(source: string[]) => patch({ source })}
          options={MESSAGE_SOURCES.map((source) => ({
            value: source,
            label: t(`commHub.source.${source}`)
          }))}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        {t("commHub.messages.filters.sentBy")}
        <Select
          aria-label={t("commHub.messages.filters.sentBy")}
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t("commHub.messages.filters.anySender")}
          value={filters.sender}
          onChange={(sender: string[]) => patch({ sender })}
          options={[
            ...pickers.senders.map((sender) => ({
              value: sender.id,
              label: sender.name
            })),
            { value: "none", label: t("commHub.messages.filters.automated") }
          ]}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        {t("commHub.messages.filters.date")}
        <DatePicker.RangePicker
          aria-label={t("commHub.messages.filters.date")}
          value={filters.range}
          allowClear
          presets={[
            {
              label: t("commHub.messages.filters.today"),
              value: [dayjs().startOf("day"), dayjs().endOf("day")]
            },
            {
              label: t("commHub.overview.periodTab.7d"),
              value: [dayjs().subtract(7, "day"), dayjs()]
            },
            {
              label: t("commHub.overview.periodTab.30d"),
              value: [dayjs().subtract(30, "day"), dayjs()]
            }
          ]}
          onChange={(values) =>
            patch({
              range: values?.[0] && values?.[1] ? [values[0], values[1]] : null
            })
          }
        />
      </label>
      {recipients.length > 0 && (
        <label className="flex flex-col gap-1 text-sm">
          {t("recordComm.filters.recipient")}
          <Select
            aria-label={t("recordComm.filters.recipient")}
            allowClear
            placeholder={t("recordComm.filters.anyRecipient")}
            value={filters.recipient ?? undefined}
            onChange={(recipient?: string) =>
              patch({ recipient: recipient ?? null })
            }
            options={recipients.map((item) => ({
              value: contactKey(item.ref),
              label: `${item.name} · ${t(`recordComm.contact.role.${item.role}`)}`
            }))}
          />
        </label>
      )}
      <label className="flex flex-col gap-1 text-sm">
        {t("commHub.table.sortBy")}
        <Select
          aria-label={t("commHub.table.sortBy")}
          value={ordering}
          onChange={setOrdering}
          options={(view === "all"
            ? ["-sent", "sent", "recipient", "-recipient", "status", "sender"]
            : ["next_attempt", "-next_attempt", "created", "-created"]
          ).map((value) => ({
            value,
            label: t(`recordComm.sort.${value.replace("-", "desc_")}`)
          }))}
        />
      </label>
    </div>
  )

  const newMessageControl = unavailableProps(!rights.send || !contact, () =>
    newMessage()
  )
  const newMessageButton = (
    <Tooltip
      title={
        !rights.send
          ? t("recordComm.header.noSendRight")
          : !contact
            ? t("recordComm.header.noContact", { record: noun })
            : undefined
      }
    >
      <Button
        type="primary"
        icon={<EditOutlined aria-hidden />}
        loading={composer.loading}
        {...newMessageControl}
      >
        {t("recordComm.toolbar.newMessage")}
      </Button>
    </Tooltip>
  )

  const toolbar = (
    <div className="flex flex-col gap-2 border-0 border-b border-solid border-slate-100 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented<RecordView>
          aria-label={t("recordComm.toolbar.view")}
          value={view}
          onChange={switchView}
          options={[
            { value: "all", label: t("recordComm.toolbar.all") },
            {
              value: "queue",
              label:
                queueCount === null
                  ? t("recordComm.toolbar.queue")
                  : t("recordComm.toolbar.queueCount", { count: queueCount })
            }
          ]}
        />
        <div className="min-w-[180px] flex-1">
          <SearchBar
            key={searchReset}
            defaultValue={filters.q}
            aria-label={t("commHub.messages.search")}
            placeholder={t("commHub.messages.search")}
            onSearch={(value: string | null) => patch({ q: value ?? "" })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          {t("commHub.messages.filters.channel")}
          <Select<ChannelFilter>
            aria-label={t("commHub.messages.filters.channel")}
            className="w-24"
            value={filters.channel}
            onChange={(channel) => patch({ channel })}
            options={[
              { value: "", label: t("recordComm.filters.allChannels") },
              { value: "email", label: t("commHub.channel.email") },
              { value: "sms", label: t("commHub.channel.sms") }
            ]}
          />
        </label>
        <Popover
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          trigger="click"
          placement="bottomRight"
          title={t("commHub.table.filters")}
          content={filterPanel}
        >
          <Badge count={filterCount} size="small" color="#2A7D8C">
            <Button
              icon={<FilterOutlined aria-hidden />}
              aria-expanded={filtersOpen}
            >
              {t("commHub.table.filters")}
            </Button>
          </Badge>
        </Popover>
        <span className="ml-auto">{newMessageButton}</span>
      </div>
      {chips.length > 0 && (
        <div
          role="group"
          aria-label={t("commHub.table.activeFilters")}
          className="flex flex-wrap items-center gap-1.5"
        >
          {chips.map((chip) => (
            <Tag
              key={chip.key}
              closable
              closeIcon={
                <CloseOutlined aria-label={t("commHub.table.removeFilter")} />
              }
              onClose={(event) => {
                event.preventDefault()
                chip.remove()
              }}
              className="m-0 rounded-full bg-[var(--dk-selected,#EEF7F6)] px-2.5 py-0.5 text-[var(--dk-primary-text,#1A5560)]"
            >
              {chip.label}
            </Tag>
          ))}
          <Button type="link" size="small" onClick={clearFilters}>
            {t("commHub.messages.filters.clear")}
          </Button>
        </div>
      )}
    </div>
  )

  const listBody = () => {
    if (state === "loading")
      return (
        <div className="p-4" aria-busy>
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      )
    if (state === "forbidden")
      return (
        <StateBlock
          icon={<LockOutlined />}
          title={t("recordComm.empty.noAccessTitle")}
          body={t("recordComm.empty.noAccessBody", { record: noun })}
        />
      )
    if (state === "failed")
      return (
        <div className="p-4">
          <Alert
            type="error"
            showIcon
            message={t("recordComm.empty.failed")}
            action={
              <Button size="small" onClick={() => void load(1)}>
                {t("commHub.common.retry")}
              </Button>
            }
          />
        </div>
      )
    if (rows.length === 0) {
      if (filterCount > 0)
        return (
          <StateBlock
            icon={<SearchOutlined />}
            title={t("recordComm.empty.noMatchTitle")}
            body={t("recordComm.empty.noMatchBody")}
            action={
              <Button onClick={clearFilters}>
                {t("commHub.messages.filters.clear")}
              </Button>
            }
          />
        )
      if (view === "queue")
        return (
          <StateBlock
            icon={<InboxOutlined />}
            title={t("recordComm.empty.queueTitle")}
            body={t("recordComm.empty.queueBody", { record: noun })}
          />
        )
      return (
        <StateBlock
          icon={<MailOutlined />}
          title={t("recordComm.empty.startTitle")}
          body={t("recordComm.empty.startBody", { record: noun })}
          action={rights.send && contact ? newMessageButton : undefined}
        />
      )
    }
    const wonDay = wonAt ? dayjs(wonAt) : null
    return (
      <>
        <ul
          aria-label={t(
            view === "queue"
              ? "recordComm.list.queueLabel"
              : "recordComm.list.label",
            { count }
          )}
          className="m-0 list-none p-0"
        >
          {rows.map((row) =>
            view === "queue" ? (
              <QueueItem
                key={row.id}
                row={row}
                selected={row.id === selected}
                onOpen={() => setSelected(row.id)}
                onActionDone={afterQueueAction}
              />
            ) : (
              <MessageItem
                key={row.id}
                row={row}
                selected={row.id === selected}
                onActionDone={afterQueueAction}
                fromLeadStage={
                  record.type === "project" &&
                  !!wonDay &&
                  dayjs(row.created).isBefore(wonDay)
                }
                onOpen={() => setSelected(row.id)}
              />
            )
          )}
        </ul>
        {rows.length < count && (
          <div className="flex justify-center p-3">
            <Button loading={loadingMore} onClick={() => void load(page + 1)}>
              {t("recordComm.list.loadMore", {
                shown: rows.length,
                total: count
              })}
            </Button>
          </div>
        )}
      </>
    )
  }

  const showPaneOnly = !wide && !!selected
  return (
    <div className="flex flex-col gap-3">
      {contact && (
        <ContactStrip
          contacts={contacts}
          selected={contact}
          onSelect={(next) => setSelectedContactKey(contactKey(next.ref))}
          onOpenPreferences={() => setPrefsOpen(true)}
        />
      )}
      <div className="overflow-hidden rounded-lg border border-solid border-slate-200 bg-surface">
        {toolbar}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,1fr)_2fr]">
          <div
            className={`border-0 border-solid border-slate-100 lg:max-h-[calc(100vh-300px)] lg:min-h-[420px] lg:overflow-y-auto lg:border-r ${
              showPaneOnly ? "hidden" : ""
            }`}
          >
            {listBody()}
          </div>
          <div
            className={`min-w-0 p-5 lg:max-h-[calc(100vh-300px)] lg:min-h-[420px] lg:overflow-y-auto ${
              showPaneOnly || wide ? "" : "hidden"
            }`}
          >
            {selected ? (
              <>
                {!wide && (
                  <Button
                    type="link"
                    className="mb-3 px-0"
                    icon={<ArrowLeftOutlined aria-hidden />}
                    onClick={() => setSelected(null)}
                  >
                    {t("commHub.messages.backToList")}
                  </Button>
                )}
                <RecordMessagePane
                  messageId={selected}
                  onNewMessage={rights.send ? newMessage : undefined}
                  onQueueAction={afterQueueAction}
                />
              </>
            ) : (
              state === "ready" &&
              rows.length > 0 && (
                <p className="py-16 text-center text-slate-500">
                  {t("commHub.messages.selectOne")}
                </p>
              )
            )}
          </div>
        </div>
      </div>
      {contact && (
        <ContactPreferencesPanel
          contact={contact}
          open={prefsOpen}
          onClose={() => setPrefsOpen(false)}
        />
      )}
      {composer.element}
    </div>
  )
}
